/**
 * Admin Service
 * Handles admin-only API operations like user provisioning
 *
 * Security: All operations require rp_admin global role
 * Rate limited: 5 burst, 2/sec sustained
 */

import { apiService, type ApiResponse } from './apiService'
import { config } from '@/config/environments'
import type {
  ProvisionUserRequest,
  ProvisionUserResponseData,
  OrganizationOption,
} from '@/types/admin.types'

class AdminService {
  /**
   * Provision a new user with optional organization setup
   *
   * Creates:
   * - Cognito user with temporary password (FORCE_CHANGE_PASSWORD state)
   * - Database user record
   * - Subscription (free tier)
   * - Organization (if orgMode='new')
   * - Membership (always created)
   *
   * @param request - User provisioning parameters
   * @returns Provisioning result with created resources
   *
   * @example
   * ```typescript
   * // Create user with new organization
   * const result = await adminService.provisionUser({
   *   email: 'user@example.com',
   *   temporaryPassword: 'TempPass123!',
   *   givenName: 'John',
   *   familyName: 'Doe',
   *   orgMode: 'new',
   *   orgName: 'Acme Corp'
   * })
   *
   * // Add user to existing organization
   * const result = await adminService.provisionUser({
   *   email: 'user@example.com',
   *   temporaryPassword: 'TempPass123!',
   *   orgMode: 'existing',
   *   orgId: 'org-uid-here',
   *   membershipRoles: ['user']
   * })
   * ```
   */
  async provisionUser(
    request: ProvisionUserRequest
  ): Promise<ApiResponse<ProvisionUserResponseData>> {
    return apiService.post<ProvisionUserResponseData>('/admin/users', request)
  }

  /**
   * Fetch list of organizations for admin dropdown selection
   * Used when provisioning user into existing organization
   *
   * Note: Uses /me/orgs endpoint which returns all orgs the user can switch to.
   * For rp_admin users, this returns ALL organizations in the system.
   *
   * @returns List of organizations with uid and name
   */
  async getOrganizations(): Promise<ApiResponse<OrganizationOption[]>> {
    try {
      const orgsUrl = `${config.authApiBaseUrl}/me/orgs`
      const response = await fetch(orgsUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      })

      if (!response.ok) {
        return {
          success: false,
          data: [],
          message: `Failed to fetch organizations: ${response.status}`,
          errors: [{ code: 'FETCH_ERROR', message: `HTTP ${response.status}` }]
        }
      }

      const wrappedResponse = await response.json()
      const orgsData = wrappedResponse.data || []

      // Transform response to OrganizationOption array
      // /me/orgs returns { id, name } format
      const organizations: OrganizationOption[] = orgsData.map((org: { id: string; name: string }) => ({
        uid: org.id,
        name: org.name
      }))

      // Sort by name
      organizations.sort((a, b) => a.name.localeCompare(b.name))

      return {
        success: true,
        data: organizations
      }
    } catch (error) {
      console.error('Error fetching organizations:', error)
      return {
        success: false,
        data: [],
        message: 'Failed to fetch organizations',
        errors: [{ code: 'NETWORK_ERROR', message: String(error) }]
      }
    }
  }
}

// Export singleton instance
export const adminService = new AdminService()
export default adminService
